import React, { useEffect, useMemo, useRef } from "react";
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
);

const REQUESTED_COLOR = "#3497D3";
const APPROVED_COLOR = "#49B870";
const PENDING_COLOR = "#f59e0b";
const REJECTED_COLOR = "#ef4444";
const VARIANCE_COLOR = "#8b5cf6";

function formatAxisNaira(value) {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `₦${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `₦${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return `₦${n.toLocaleString()}`;
}

function lineSeries(label, data, color, dense) {
  return {
    label,
    data,
    borderColor: color,
    backgroundColor: color,
    borderWidth: 1.5,
    pointRadius: dense ? 0 : 2,
    pointHoverRadius: 4,
    tension: 0.3,
    fill: false,
  };
}

/**
 * Compact PA cost trend: requested and approved as areas; pending, rejected and the signed
 * net variance (optional) as lines. Uses chart.js (already a project dependency).
 */
export default function PACostTrendChart({
  labels,
  requested,
  approved,
  pending,
  rejected,
  variance,
  encounters,
  items,
  requestedLabel = "Requested",
  approvedLabel = "Approved",
  formatMoney,
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const money = useMemo(
    () => formatMoney || ((v) => `₦${Number(v || 0).toLocaleString()}`),
    [formatMoney],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !labels?.length) return undefined;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const dense = labels.length > 14;
    chartRef.current = new Chart(canvas, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: requestedLabel,
            data: requested,
            borderColor: REQUESTED_COLOR,
            backgroundColor: "rgba(52, 151, 211, 0.12)",
            borderWidth: 2,
            pointRadius: dense ? 0 : 3,
            pointHoverRadius: 5,
            tension: 0.3,
            fill: true,
          },
          {
            label: approvedLabel,
            data: approved,
            borderColor: APPROVED_COLOR,
            backgroundColor: "rgba(73, 184, 112, 0.14)",
            borderWidth: 2,
            pointRadius: dense ? 0 : 3,
            pointHoverRadius: 5,
            tension: 0.3,
            fill: true,
          },
          ...(pending ? [lineSeries("Pending", pending, PENDING_COLOR, dense)] : []),
          ...(rejected ? [lineSeries("Rejected", rejected, REJECTED_COLOR, dense)] : []),
          ...(variance
            ? [{ ...lineSeries("Net variance", variance, VARIANCE_COLOR, dense), borderDash: [4, 4] }]
            : []),
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "top",
            align: "end",
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              usePointStyle: true,
              pointStyle: "circle",
              color: "#64748b",
              font: { size: 12 },
              padding: 16,
            },
          },
          tooltip: {
            backgroundColor: "#0f172a",
            titleFont: { size: 12, weight: "600" },
            bodyFont: { size: 12 },
            padding: 10,
            callbacks: {
              afterBody(tooltipItems) {
                const idx = tooltipItems?.[0]?.dataIndex;
                if (idx == null) return [];
                const lines = [];
                if (items) {
                  const n = items[idx] ?? 0;
                  lines.push(`${n} PA item${n === 1 ? "" : "s"}`);
                }
                if (encounters) {
                  const count = encounters[idx] ?? 0;
                  lines.push(`${count} encounter${count === 1 ? "" : "s"}`);
                }
                return lines;
              },
              label(ctx) {
                const label = ctx.dataset?.label || "";
                const y = Number(ctx.parsed?.y) || 0;
                const sign = ctx.dataset?.borderDash && y > 0 ? "+" : "";
                return `${label}: ${sign}${money(y)}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: "#94a3b8",
              font: { size: 11 },
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: dense ? 8 : 12,
            },
          },
          y: {
            beginAtZero: true,
            grid: { color: "#f1f5f9" },
            border: { display: false },
            ticks: {
              color: "#94a3b8",
              font: { size: 11 },
              callback: (value) => formatAxisNaira(value),
              maxTicksLimit: 5,
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [labels, requested, approved, pending, rejected, variance, encounters, items, requestedLabel, approvedLabel, money]);

  return (
    <div className="pa-trend-chart" role="img" aria-label="PA cost trend chart">
      <canvas ref={canvasRef} />
    </div>
  );
}
